import { Link } from "react-router-dom";
import { Globe, Code, TrendingUp, Bot, ArrowRight, ArrowUpRight } from "lucide-react";
import Navbar from "../components/Navbar";
import PageMeta from "../components/PageMeta";
import Footer from "../components/Footer";
import FloatingFAQ from "../components/FloatingFAQ";

const services = [
  {
    icon: Globe,
    title: "Web Development",
    description: "From stunning landing pages to full e-commerce platforms. We build fast, secure, and scalable websites that represent your brand.",
    link: "/services/web-development",
    highlights: ["Custom Design", "E-Commerce", "CMS"]
  },
  {
    icon: Code,
    title: "Custom Software",
    description: "Automate workflows, manage data, and scale operations with software built specifically for your business needs.",
    link: "/services/custom-software",
    highlights: ["CRM Systems", "Dashboards", "APIs"]
  },
  {
    icon: Bot,
    title: "AI Automation",
    description: "Leverage artificial intelligence to streamline operations, enhance customer experiences, and unlock new business possibilities.",
    link: "/services/ai-automation",
    highlights: ["Chatbots", "Process Automation", "AI Integration"]
  },
  {
    icon: TrendingUp,
    title: "Growth Marketing",
    description: "SEO, paid ads, and content strategies that put your business in front of the right audience.",
    link: "/services/growth-marketing",
    highlights: ["SEO", "PPC", "Content"]
  }
];

export default function Services() {
  return (
    <div className="noise-bg">
      <PageMeta
        title="Services | Averoan Technologies"
        description="Web development, custom software, AI automation, and growth marketing services for businesses in Vancouver and beyond."
      />
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-16 relative" data-testid="services-hero">
        <div className="hero-glow absolute top-0 right-0 opacity-30" />
        <div className="container-main relative z-10">
          <div className="max-w-4xl">
            <span className="tag mb-6">Services</span>
            <h1
              className="text-5xl md:text-7xl lg:text-8xl font-bold mb-6"
              style={{ fontFamily: '"Clash Display", sans-serif' }}
            >
              What we<br />
              <span style={{ color: '#D4FF00' }}>do best</span>
            </h1>
            <p
              className="text-base md:text-lg max-w-lg"
              style={{ color: '#A1A1AA' }}
            >
              Comprehensive digital solutions to help your business thrive in the online world.
            </p>
          </div>
        </div>
      </section>

      {/* Services Cards */}
      <section className="section-padding" data-testid="services-grid">
        <div className="container-main">
          <div className="flex flex-col gap-8">
            {services.map((service, index) => (
              <Link
                key={service.title}
                to={service.link}
                className="group block p-8 md:p-12 rounded-2xl transition-all duration-300 hover:border-[#D4FF00]"
                style={{ background: '#18181B', border: '2px solid rgba(255,255,255,0.1)' }}
                data-testid={`service-card-${index}`}
              >
                <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-12">
                  <div
                    className="w-16 h-16 rounded-xl flex items-center justify-center flex-shrink-0 transition-all duration-300 group-hover:scale-110"
                    style={{ background: 'rgba(212, 255, 0, 0.1)', border: '1px solid rgba(212, 255, 0, 0.2)' }}
                  >
                    <service.icon size={28} style={{ color: '#D4FF00' }} />
                  </div>

                  <div className="flex-1">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-3">
                      <h3
                        className="text-2xl md:text-3xl font-bold normal-case group-hover:text-[#D4FF00] transition-colors"
                        style={{ fontFamily: '"Clash Display", sans-serif' }}
                      >
                        {service.title}
                      </h3>
                      <div className="flex gap-2">
                        {service.highlights.map((highlight) => (
                          <span
                            key={highlight}
                            className="px-3 py-1 text-xs rounded-full"
                            style={{ background: 'rgba(255,255,255,0.05)', color: '#A1A1AA' }}
                          >
                            {highlight}
                          </span>
                        ))}
                      </div>
                    </div>
                    <p className="text-sm md:text-base" style={{ color: '#A1A1AA' }}>
                      {service.description}
                    </p>
                  </div>

                  <div className="flex-shrink-0">
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center transition-all duration-300 group-hover:bg-[#D4FF00]"
                      style={{ border: '1px solid rgba(255,255,255,0.1)' }}
                    >
                      <ArrowUpRight
                        size={20}
                        className="transition-colors group-hover:text-black"
                        style={{ color: '#A1A1AA' }}
                      />
                    </div>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="section-padding" style={{ background: '#18181B' }}>
        <div className="container-main">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: "50+", label: "Projects Delivered" },
              { value: "98%", label: "Client Satisfaction" },
              { value: "5+", label: "Years Experience" },
              { value: "24/7", label: "Support Available" }
            ].map((stat) => (
              <div key={stat.label} className="text-center">
                <div
                  className="text-4xl md:text-5xl font-bold mb-2"
                  style={{ color: '#D4FF00', fontFamily: '"Clash Display", sans-serif' }}
                >
                  {stat.value}
                </div>
                <p className="text-sm" style={{ color: '#A1A1AA' }}>{stat.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding" data-testid="services-cta">
        <div className="container-main">
          <div
            className="relative overflow-hidden rounded-2xl p-8 md:p-16 text-center"
            style={{ background: 'linear-gradient(135deg, #18181B 0%, #1E1E22 100%)', border: '1px solid rgba(212,255,0,0.1)' }}
          >
            <div className="hero-glow absolute top-0 left-1/2 -translate-x-1/2 opacity-20" />
            <div className="relative z-10">
              <h2
                className="text-3xl md:text-5xl lg:text-6xl font-bold mb-6"
                style={{ fontFamily: '"Clash Display", sans-serif' }}
              >
                Ready to <span style={{ color: '#D4FF00' }}>get started?</span>
              </h2>
              <p className="text-base md:text-lg mb-10 max-w-xl mx-auto" style={{ color: '#A1A1AA' }}>
                Let's discuss your project and find the perfect solution for your business needs.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Link to="/consultation" className="btn-primary" data-testid="services-cta-btn">
                  Start a Project <ArrowRight size={16} />
                </Link>
                <Link to="/portfolio" className="btn-outline">
                  View Our Work <ArrowUpRight size={16} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
      <FloatingFAQ />
    </div>
  );
}
